import React, { useEffect, useState } from "react";
import { doc, getDoc, getDocs, limit, Timestamp, where } from "firebase/firestore";
import { useParams, Link } from "react-router-dom";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import NavBar from "../../components/feature/NavBar";
import VideoHeader from "../../components/feature/VideoHeader";
import VideoPlayer from "../../components/feature/VideoPlayer";
import Tabs from "../../components/token/Tabs";
import AddBoardPopUp from "../../components/token/AddBoardPopUp";
import Footer from "../../components/feature/Footer";
import { db } from "../../utils/firebase";
import { useAuth } from "../../utils/AuthContext";
import { usePageMeta } from "../../hooks/usePageMeta";
import { normalizeEntity, publishedVideosQuery } from "../../lib/catalog";
import { fallbackThumbnail } from "../../lib/videoSources";
import { Entity, Video } from "../../types/cms";

export type { Entity, Video } from "../../types/cms";

const PAGE = 12;

async function loadEntity(primary: "companies" | "agencies", id?: string): Promise<Entity | null> {
  if (!id) return null;
  const secondary = primary === "companies" ? "agencies" : "companies";
  for (const name of [primary, secondary]) {
    const snap = await getDoc(doc(db, name, id));
    if (snap.exists()) return normalizeEntity(snap.id, snap.data());
  }
  return null;
}

const VideoPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAdmin } = useAuth();
  const [video, setVideo] = useState<Video | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [agency, setAgency] = useState<Entity | null>(null);
  const [enterprise, setEnterprise] = useState<Entity | null>(null);
  const [relatedVideos, setRelatedVideos] = useState<Video[]>([]);
  const [activeTab, setActiveTab] = useState<string>("Styleframes");
  const [visibleStyleframesCount, setVisibleStyleframesCount] = useState<number>(PAGE);
  const [hoveredStyleframeId, setHoveredStyleframeId] = useState<string | null>(null);
  const [displayBoardChoice, setDisplayBoardChoice] = useState<string | null>(null);

  usePageMeta(
    video ? `${video.title}${enterprise ? ` — ${enterprise.name}` : ""}` : undefined,
    video?.description,
  );

  // Main video + brand + studio.
  useEffect(() => {
    if (!id) return;
    let active = true;
    setVideo(null);
    setNotFound(false);
    setEnterprise(null);
    setAgency(null);
    setRelatedVideos([]);
    setVisibleStyleframesCount(PAGE);
    (async () => {
      try {
        const snap = await getDoc(doc(db, "videos", id));
        if (!active) return;
        if (!snap.exists()) {
          setNotFound(true);
          return;
        }
        const data = { ...(snap.data() as Video), id: snap.id };
        setVideo(data);
        const [company, studio] = await Promise.all([
          loadEntity("companies", data.enterpriseId),
          loadEntity("agencies", data.agencyId),
        ]);
        if (!active) return;
        setEnterprise(company);
        setAgency(studio);
      } catch (error) {
        // Drafts are unreadable for visitors: treat as not found.
        console.error("Error fetching video:", error);
        if (active) setNotFound(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  // Up to 3 other published videos from the same brand.
  useEffect(() => {
    setRelatedVideos([]);
    if (!video?.enterpriseId) return;
    let active = true;
    getDocs(publishedVideosQuery(where("enterpriseId", "==", video.enterpriseId), limit(4)))
      .then((snap) => {
        if (!active) return;
        setRelatedVideos(
          snap.docs
            .filter((d) => d.id !== video.id)
            .slice(0, 3)
            .map((d) => ({ ...(d.data() as Video), id: d.id })),
        );
      })
      .catch((error) => console.error("Error fetching related videos:", error));
    return () => {
      active = false;
    };
  }, [video?.id, video?.enterpriseId]);

  const formatDate = (timestamp: Timestamp) =>
    timestamp.toDate().toLocaleDateString("fr-FR", { year: "numeric", month: "short", day: "numeric" });

  const tabs: string[] = [];
  if (video?.styleframesURLs?.length) tabs.push("Styleframes");
  if (video?.script?.trim()) tabs.push("Script");
  const currentTab = tabs.includes(activeTab) ? activeTab : tabs[0];

  return (
    <div className="min-h-screen bg-neutral-50 text-gray-800 md:px-20 px-8">
      <NavBar />
      {notFound ? (
        <div className="px-4 py-20 flex flex-col gap-3">
          <h1 className="text-3xl font-bold">This video isn't available</h1>
          <p className="text-neutral-600">It may have been removed or isn't published yet.</p>
          <Link to="/" className="underline">
            Discover other videos
          </Link>
        </div>
      ) : video ? (
        <div
          className="max-w-[100vw] mx-auto px-4 py-6 flex flex-col gap-16 w-full"
          onClick={() => setDisplayBoardChoice(null)}
        >
          <div className="flex flex-col gap-8 w-full">
            {isAdmin && (
              <div className="flex flex-wrap items-center gap-3 text-sm bg-neutral-900 text-white rounded-md px-4 py-2">
                {video.status === "draft" ? "Brouillon : visible uniquement par les admins." : "Vue admin"}
                <Link to={`/admin/video/${video.id}`} className="ml-auto underline">
                  Modifier la fiche
                </Link>
              </div>
            )}
            <VideoHeader video={video} formatDate={formatDate} agency={agency} company={enterprise} />
            <section className="bg-white rounded-lg overflow-hidden shadow mb-8">
              <VideoPlayer video={video} />
            </section>
          </div>

          {tabs.length > 0 && (
            <div className="flex flex-col gap-5 w-full">
              <Tabs tabs={tabs} activeTab={currentTab} setActiveTab={setActiveTab} />
              <div className="min-h-[200px]">
                {currentTab === "Styleframes" && (
                  <div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {video.styleframesURLs.slice(0, visibleStyleframesCount).map((imgUrl, idx) => (
                        <div
                          key={imgUrl}
                          className="relative w-full min-h-40 bg-gray-100 rounded"
                          onMouseEnter={() => setHoveredStyleframeId(String(idx))}
                          onMouseLeave={() => setHoveredStyleframeId(null)}
                          onClick={(e) => {
                            if (displayBoardChoice === String(idx)) e.stopPropagation();
                          }}
                        >
                          <img
                            src={imgUrl}
                            alt={`${video.title} — styleframe ${idx + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                          {(hoveredStyleframeId === String(idx) || displayBoardChoice === String(idx)) && (
                            <div className="absolute inset-0 flex p-4 justify-end gap-4 z-[100]">
                              <div className="flex flex-col justify-start items-start gap-4">
                                <button
                                  className="ml-auto bg-neutral-600 text-white px-4 py-2 rounded-full h-max flex items-center gap-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayBoardChoice(String(idx));
                                  }}
                                >
                                  Tableau <ChevronDownIcon className="h-6 w-6 text-gray-300" />
                                </button>
                                {displayBoardChoice === String(idx) && (
                                  <AddBoardPopUp videoId={video.id} styleframeURL={imgUrl} />
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex justify-center gap-4">
                      {visibleStyleframesCount > PAGE && (
                        <button
                          onClick={() => setVisibleStyleframesCount(PAGE)}
                          className="px-4 py-2 bg-neutral-900 text-white rounded-full hover:bg-neutral-950"
                        >
                          See Less
                        </button>
                      )}
                      {video.styleframesURLs.length > visibleStyleframesCount && (
                        <button
                          onClick={() => setVisibleStyleframesCount((c) => c + 24)}
                          className="px-4 py-2 bg-neutral-900 text-white rounded-full hover:bg-neutral-950"
                        >
                          See More
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {currentTab === "Script" && (
                  <div className="p-4 bg-gray-100 rounded">
                    <pre className="text-sm whitespace-pre-wrap font-sans">{video.script}</pre>
                  </div>
                )}
              </div>
            </div>
          )}

          {relatedVideos.length > 0 && (
            <section className="mt-10">
              <h2 className="text-xl font-bold mb-4">More from {enterprise?.name ?? "this brand"}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {relatedVideos.map((related) => {
                  const thumb = related.thumbnailURL || fallbackThumbnail(related.videoURL);
                  return (
                    <Link to={`/video/${related.id}`} key={related.id} className="flex flex-col gap-2">
                      {thumb ? (
                        <img src={thumb} alt="" className="w-full object-cover aspect-video" loading="lazy" />
                      ) : (
                        <div className="w-full aspect-video bg-gray-200" />
                      )}
                      <span className="font-semibold truncate">{related.title}</span>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      ) : (
        <div className="w-full flex items-center justify-center pt-20">
          <span className="text-gray-500 animate-pulse">Loading video…</span>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default VideoPage;
